/**
 * React Query Client Factory
 * 
 * Creates a configured QueryClient instance following project guidelines.
 * 
 * @see docs/state-management-guide.md
 * @see rules/210-state-management.md
 */

import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';

import { reportApiFailure } from '@/lib/api-client';

export function createQueryClient(): QueryClient {
  return new QueryClient({
    // 報告境界（#8046）。Cache の onError は再試行を終えた最終失敗で 1 回だけ呼ばれ、
    // observer 数に依存しない。想定外の ApiRequestError だけが報告される。
    queryCache: new QueryCache({ onError: reportApiFailure }),
    mutationCache: new MutationCache({ onError: reportApiFailure }),
    defaultOptions: {
      queries: {
        // Cache and stale time settings
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000,   // 10 minutes (formerly cacheTime)
        
        // Retry settings
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        
        // Background refetch settings
        // IMPORTANT: Disable refetch on window focus to prevent UI rebuilds when switching tabs
        // @see https://github.com/Atsumell/kakusill/issues/616
        refetchOnWindowFocus: false,
        refetchOnReconnect: 'always',
        
        // Error handling
        throwOnError: false,
      },
      mutations: {
        // Optimistic update defaults
        retry: 1,
        throwOnError: false,
      },
    },
  });
}
